import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Zap, Loader2, Gift } from "lucide-react";
import { toast } from "sonner";
import Logo from "../components/Logo";

export default function Register() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "", referral_code: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const ref = params.get("ref");
    if (ref) setForm((f) => ({ ...f, referral_code: ref }));
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/register", form);
      login(data.token, data.user);
      toast.success("Account created! Upload your first bill to earn.");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F8FAFC]">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 p-12 text-white">
        <Link to="/" className="flex items-center gap-2">
          <Logo textClassName="text-white" tagline />
        </Link>
        <div>
          <h2 className="font-display text-4xl font-bold leading-tight">One app for<br /><span className="text-emerald-400">every bill.</span></h2>
          <p className="text-slate-400 mt-4 max-w-sm">Sign up free to pay bills across categories, unlock discounts, earn reward points and switch providers to save.</p>
        </div>
        <p className="text-slate-500 text-sm">© 2026 HARMONEYS · Enjoy The Difference</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <Logo />
          </Link>
          <h1 className="font-display text-3xl font-bold text-slate-900">Create your account</h1>
          <p className="text-slate-500 mt-2">Start earning rewards in minutes.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="name" className="font-semibold">Full name</Label>
              <Input id="name" required value={form.name} onChange={set("name")} data-testid="register-name" placeholder="Jane Doe" className="mt-1.5 bg-slate-50 py-5" />
            </div>
            <div>
              <Label htmlFor="email" className="font-semibold">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={set("email")} data-testid="register-email" placeholder="you@example.com" className="mt-1.5 bg-slate-50 py-5" />
            </div>
            <div>
              <Label htmlFor="password" className="font-semibold">Password</Label>
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="register-password" placeholder="Min 6 characters" className="mt-1.5 bg-slate-50 py-5" />
            </div>
            <div>
              <Label htmlFor="ref" className="font-semibold flex items-center gap-1.5"><Gift className="w-4 h-4 text-emerald-500" /> Referral code <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input id="ref" value={form.referral_code} onChange={set("referral_code")} data-testid="register-referral" placeholder="REEXXXXXX" className="mt-1.5 bg-slate-50 py-5 uppercase" />
            </div>
            {error && <p className="text-sm text-red-600 font-medium" data-testid="register-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600 font-bold py-6">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Create account"}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            Already have an account? <Link to="/login" className="text-emerald-600 font-semibold hover:underline" data-testid="go-login">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
